import { isInteger } from 'lodash';

import { getSchemaForFieldPath, nextLinkSchema } from 'dashboard/components/JsonSchemaSelect/utils';
import {
  Chain,
  FunctionParamType,
  isValid,
  methodChainNodeToJsonSchema,
  MethodChainNodeTypes,
  methodChainToString,
  parseMethodChain,
} from 'dashboard/lib/workflows/method-chain';
import { UsableObjectConfig } from 'dashboard/lib/workflows/objects';
import {
  getPlatformObjectTypeFromJsonSchema,
  getSchemaId,
  resolveJssRef,
} from 'dashboard/lib/workflows/platform-object';
import { StepContext } from 'dashboard/lib/workflows/validation/validateActionStep';
import { WorkflowActionMeta, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowStep, WorkflowStepInput } from 'dashboard/models/workflow-version';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import {
  functionParametersFromJsonSchema,
  isFunctionSchema,
  isJsonSchemaRef,
  JsonSchema,
} from 'lib/json-schema';
import { TokensToNode } from 'lib/workflows';

export interface WorkflowValidationContext {
  stepsConfig: WorkflowStep[];
  inputStep: WorkflowStepInput;
  graph: TokensToNode;
  workflowSchema: WorkflowSchema;
  actionMeta: WorkflowActionMeta;
  featureFlags: FeatureFlagsMap;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  getContextForStep: (activeStepToken: string) => {
    usableObjects: UsableObjectConfig[];
    jsonSchema: JsonSchema;
  };
}

export const fieldPathHasValidSchema = (
  path: string,
  stepContext: Pick<StepContext, 'jsonSchema'>,
  validationContext: WorkflowValidationContext,
): { valid: boolean; errorMessages: string[] } => {
  const {
    platformObjectJsonSchema,
    workflowSchema: { primitives: primitiveUtilities },
    featureFlags,
  } = validationContext;

  if (!featureFlags['dashboard:workflow-method-chain-v2-validation']) {
    if (
      getSchemaForFieldPath({
        path,
        initialSchema: stepContext.jsonSchema,
        platformObjectJsonSchema,
      }).schema
    ) {
      return { valid: true, errorMessages: [] };
    } else {
      return { valid: false, errorMessages: [`unable to resolve path ${path}`] };
    }
  }
  if (path === '') {
    return { valid: false, errorMessages: ['invalid empty path'] };
  }
  if (!isValid(path)) {
    return { valid: false, errorMessages: [`invalid path ${path}`] };
  }
  const schema = isJsonSchemaRef(stepContext.jsonSchema)
    ? resolveJssRef(stepContext.jsonSchema, platformObjectJsonSchema)
    : stepContext.jsonSchema;

  const methodChain = parseMethodChain(path);

  const { errors } = validateRecursivePathSchema({
    methodChain,
    schema,
    platformObjectJsonSchema,
    primitiveUtilities,
  });

  return { valid: errors.length === 0, errorMessages: errors };
};

const validateRecursivePathSchema = ({
  methodChain,
  schema,
  platformObjectJsonSchema,
  primitiveUtilities,
}: {
  methodChain: Chain;
  schema: JsonSchema;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  primitiveUtilities: ObjectSchemaMap;
}): { schema: JsonSchema | null; errors: string[] } => {
  if (!methodChain) {
    return { schema: null, errors: ['invalid empty path'] };
  }
  const errors: string[] = [];
  const result = methodChain.links.reduce<JsonSchema | null>((currentSchema, link) => {
    if (currentSchema && 'type' in currentSchema) {
      const schemaId = getSchemaId(currentSchema.type);
      if (schemaId in primitiveUtilities) {
        const functionSchema = primitiveUtilities[schemaId].properties[link.name];
        const isFunction = isFunctionSchema(functionSchema);
        if (link.type === MethodChainNodeTypes.Function) {
          if (!isFunction) {
            errors.push(`unknown function ${link.name} for ${currentSchema.type}`);
          } else {
            const params = functionParametersFromJsonSchema(functionSchema, currentSchema);
            if (params.length !== link.params.length) {
              errors.push(
                `function ${link.name} expects ${params.length} parameters but got ${link.params.length}`,
              );
            } else {
              params.forEach((expectedSchema, index) => {
                const actualParam = link.params[index];
                let actualSchema: JsonSchema | null;
                if (actualParam.type === MethodChainNodeTypes.Chain) {
                  const { schema: recursiveSchema, errors: paramErrors } =
                    validateRecursivePathSchema({
                      methodChain: actualParam,
                      schema,
                      platformObjectJsonSchema,
                      primitiveUtilities,
                    });
                  actualSchema = recursiveSchema;
                  errors.push(...paramErrors);
                } else {
                  actualSchema = methodChainNodeToJsonSchema(actualParam);
                }

                if (!paramIsAcceptable(expectedSchema, actualSchema, actualParam)) {
                  errors.push(
                    `parameter ${methodChainToString(actualParam)} should be of type ${extractSchemaType(expectedSchema)}`,
                  );
                }
              });
            }
          }
        } else if (link.type === MethodChainNodeTypes.Identifier && isFunction) {
          errors.push(`${link.name} should be a function but no parameter specified`);
        }
      }
    }

    return nextLinkSchema({
      currentSchema,
      nextLink: link.name,
      platformObjectJsonSchema,
      primitiveUtilities,
    });
  }, schema);

  if (!result) {
    errors.push(`unable to resolve path ${methodChainToString(methodChain)}`);
  }
  return { schema: result, errors };
};

const paramIsAcceptable = (
  expected: JsonSchema | null,
  actual: JsonSchema | null,
  actualParam: FunctionParamType,
): boolean => {
  if (!expected || !actual) {
    return false;
  }

  const expectedSchemaType = extractSchemaType(expected);
  const actualSchemaType = extractSchemaType(actual);

  if (expectedSchemaType === 'integer' && actualSchemaType === 'number' && 'value' in actualParam) {
    return isInteger(actualParam.value);
  }
  if (expectedSchemaType === 'number' && actualSchemaType === 'integer') {
    return true;
  }

  return expectedSchemaType === actualSchemaType;
};

const extractSchemaType = (schema: JsonSchema): string => {
  const platformObjectSchemaType = getPlatformObjectTypeFromJsonSchema(schema);
  if (platformObjectSchemaType) {
    return platformObjectSchemaType;
  }

  const jssId = schema['$id'] || schema['$ref'];
  if (jssId) {
    return jssId.split('/')[2];
  }

  if ('type' in schema) {
    // This check includes 'object' type, which means all non-persona objects are considered equivalent
    // Since we don't perform strict deep schema checks, we will fail open for validation for now.
    return schema.type;
  }

  return 'unknown';
};
